import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElements, loadStripe } from '@stripe/stripe-js';
import { StripeElement } from 'components';
import { useStripeProviderOptions } from 'hooks';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { selectors, useAppSelector } from 'store';

// const stripe = loadStripe(
//   'pk_test_51LXSUCKVvYcL9iJMtHoNKbxATispyaUJF0jQGGUB5qdA0YWRGkEniVsBUsdepUEm4kdPAO0DmDdBoxGXlL6z7aYy00vJ7DQ1mY' || '',
// );

type StripeProviderProps = {
  children: ReactNode;
  showEmptyState?: boolean;
  setStripe?: (stripe: Stripe | null) => void;
  setElements?: (elements: StripeElements | null) => void;
  amount?: number;
  mode?: 'payment' | 'setup' | 'subscription';
  onConfirmExpressCheckout?: () => void;
  clientSecret?: string;
};

const StripeProvider: FC<StripeProviderProps> = ({
  children,
  showEmptyState = false,
  setStripe,
  setElements,
  onConfirmExpressCheckout,
  clientSecret,
}) => {
  const direction = useAppSelector(selectors.direction.selectDirection);
  const [stripe, setStripeInstance] = useState<Stripe | null>(null);

  // The direction payload is the canonical source of the publishable key (multi-tenant: each
  // direction runs its own Stripe account). The env fallback exists for localhost, where the
  // direction lookup can 404 (no row carries the localhost origin) or Redux can hold a stale
  // persisted direction without the key — either way Elements silently never loaded.
  const publicKey = direction?.stripePublicKey || process.env.REACT_APP_PUBLISHABLE_KEY;

  useEffect(() => {
    const loadStripeInstance = async () => {
      if (publicKey) {
        const stripeInstance = await loadStripe(publicKey);
        setStripeInstance(stripeInstance);
        setStripe?.(stripeInstance);
      }
    };

    loadStripeInstance();
  }, [publicKey]);

  useEffect(() => {
    const handleMount = async () => {
      setStripe?.(await stripe);
    };

    handleMount();
  }, [stripe]);

  const { options } = useStripeProviderOptions(Boolean(clientSecret));
  const { appearance, fonts, locale } = options;

  if (!clientSecret && !showEmptyState) {
    return null;
  }

  if (!clientSecret) {
    return (
      <Elements
        stripe={stripe}
        key="elements without client secret"
        options={{
          ...{
            mode: 'setup',
            currency: 'usd',
            appearance,
            fonts,
            locale,
          },
        }}>
        {children}
      </Elements>
    );
  }

  const ExpressCheckout = !!onConfirmExpressCheckout && (
    <ExpressCheckoutElement
      onConfirm={onConfirmExpressCheckout}
      options={{
        wallets: { applePay: 'always', googlePay: 'always' },
        layout: { maxColumns: 1, overflow: 'never' },
      }}
      onClick={({ resolve }) => {
        resolve({
          emailRequired: true,
        });
      }}
    />
  );

  return (
    <>
      <Elements stripe={stripe} key={clientSecret} options={{ appearance, locale, fonts, clientSecret }}>
        {ExpressCheckout}
        <StripeElement setElements={setElements}>{children}</StripeElement>
      </Elements>
    </>
  );
};

export default StripeProvider;
